import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Sparkles } from "lucide-react";

const TEAL = "#00E5FF";

const INDIVIDUAL = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    cadence: "always",
    tagline: "Read and study, freely.",
    cta: "Start free",
    features: [
      "Every translation — NIV, KJV, WEB, ASV, NKJV, AMP",
      "Strong's concordance, Word Trails & author fingerprints",
      "Unlimited reading, notes, prayers & study history",
      "Simple / Standard reading levels",
      "15 study credits each month",
      "Go Deeper, Devotionals & Discussion Questions (credit-based)",
    ],
  },
  {
    id: "basic",
    name: "Basic",
    price: "$7",
    cadence: "per month",
    tagline: "For steady, daily study.",
    cta: "Go deeper, daily",
    features: [
      "Everything in Free",
      "Unlimited Go Deeper conversations",
      "100 study credits each month",
      "Plenty of room to Go Deeper and create every day",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "$16.99",
    cadence: "per month",
    tagline: "For those who create as they study.",
    cta: "Unlock the toolkit",
    badge: "Most loved",
    features: [
      "Everything in Basic",
      "The full suite of creation tools",
      "The largest monthly study-credit pool",
      "250 study credits each month",
    ],
  },
];

const TEAM = [
  {
    id: "team",
    name: "Team",
    price: "$34",
    cadence: "per month",
    tagline: "For groups studying together.",
    cta: "Equip your group",
    features: [
      "Everything in Pro",
      "Built for churches, classes, small groups and book clubs",
      "Room for a whole group to study, prepare and create",
      "600 study credits each month",
    ],
  },
];

export const PricingCard = ({ tier, onStart }) => (
  <div
    data-testid={`pricing-card-${tier.id}`}
    className={`relative rounded-2xl border p-6 bg-[#0E131F] flex flex-col transition-all duration-300 hover:-translate-y-0.5 ${
      tier.badge ? "border-[#00E5FF]/60 ring-2 ring-[#00E5FF]/25 shadow-sm" : "border-[#1E273D]"
    }`}
  >
    {tier.badge && (
      <div
        data-testid="pricing-badge"
        className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-[#00E5FF] text-[#07090E] text-xs font-medium px-3 py-1 rounded-full"
      >
        <Sparkles className="w-3 h-3" strokeWidth={1.5} /> {tier.badge}
      </div>
    )}
    <div className="text-2xl text-white">{tier.name}</div>
    <div className="mt-2 flex items-baseline gap-1.5">
      <span className="text-3xl font-medium text-white">{tier.price}</span>
      <span className="text-sm text-slate-500">/ {tier.cadence}</span>
    </div>
    <p className="text-sm text-slate-400 mt-2 leading-relaxed">{tier.tagline}</p>
    <ul className="mt-5 space-y-2.5 flex-1">
      {tier.features.map((f, i) => (
        <li key={i} className="flex gap-2.5 text-sm text-slate-400 leading-relaxed">
          <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: TEAL }} strokeWidth={2} />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <Button
      data-testid={`pricing-cta-${tier.id}`}
      onClick={() => onStart(tier)}
      className={`mt-6 h-11 rounded-full text-base font-medium ${
        tier.badge
          ? "bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E]"
          : "bg-[#0E131F] border border-[#29344D] text-slate-200 hover:border-[#00E5FF]/50 hover:bg-[#10222A]"
      }`}
    >
      {tier.cta}
    </Button>
  </div>
);

export default function Pricing() {
  const navigate = useNavigate();
  const [view, setView] = useState("you");
  const start = () => navigate("/study"); // logged-out visitors are routed to sign in
  const tiers = view === "you" ? INDIVIDUAL : TEAM;

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-200">
      {/* NAV */}
      <nav className="sticky top-0 z-40 bg-[#07090E]/85 backdrop-blur-xl border-b border-[#1E273D]">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="pricing-logo">
            <img src="/openword-logo.png" alt="OpenWord AI" className="w-8 h-8" />
            <span className="text-xl text-white">OpenWord AI</span>
          </button>
          <Button
            onClick={start}
            data-testid="pricing-nav-open"
            variant="ghost"
            className="rounded-full text-[#00E5FF] hover:bg-[#10222A] h-10 px-5"
          >
            Open the Word
          </Button>
        </div>
      </nav>

      {/* HEADER + objection opener */}
      <header className="max-w-3xl mx-auto px-6 pt-20 pb-10 text-center">
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-3">Plans</div>
        <h1 className="text-4xl sm:text-5xl text-white leading-tight">
          Simple plans. The text stays open.
        </h1>
        <p className="mt-6 text-lg text-slate-400 leading-relaxed">
          Scripture is free — and it always will be. Every translation, the Strong&apos;s concordance with Word
          Trails, your notes and your full history are open to everyone, unlimited. A plan simply adds study
          credits for the deeper AI tools — like Go Deeper conversations — plus the creation toolkit.
        </p>
      </header>

      {/* TOGGLE */}
      <div className="flex justify-center px-6">
        <div className="inline-flex items-center bg-[#0E131F] border border-[#1E273D] rounded-full p-1" data-testid="pricing-toggle">
          {[
            ["you", "For You"],
            ["teams", "For Teams & Groups"],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`pricing-toggle-${id}`}
              onClick={() => setView(id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                view === id ? "bg-[#00E5FF] text-[#07090E]" : "text-slate-400 hover:text-[#00E5FF]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* CARDS */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className={`grid gap-5 ${view === "you" ? "md:grid-cols-3 max-w-4xl mx-auto" : "max-w-md mx-auto"}`}>
          {tiers.map((t) => (
            <PricingCard key={t.id} tier={t} onStart={start} />
          ))}
        </div>

        {view === "teams" && (
          <p className="text-center text-sm text-slate-400 mt-8">
            Studying on your own?{" "}
            <button onClick={() => setView("you")} className="text-[#00E5FF] underline" data-testid="pricing-see-individual">
              See individual plans
            </button>
            .
          </p>
        )}

        <div className="mt-14 max-w-2xl mx-auto rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 text-center">
          <p className="text-slate-300 leading-relaxed">
            Every plan starts free, and you can change or cancel anytime from Settings. Credits renew each month;
            reading, notes and history never cost a thing. Whoever you are, wherever you&apos;re starting from — come and read.
          </p>
          <div className="mt-5">
            <Button
              onClick={start}
              data-testid="pricing-bottom-cta"
              className="h-12 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-8 text-base font-medium"
            >
              Start free
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#1E273D] bg-[#0E131F]">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-center md:text-left">
          <div className="text-lg text-slate-200">OpenWord AI</div>
          <div className="flex items-center justify-center gap-5 text-sm text-slate-500 flex-wrap">
            <button onClick={() => navigate("/")} className="hover:text-[#00E5FF]">Home</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-[#00E5FF]">Privacy</button>
            <button onClick={() => navigate("/terms")} className="hover:text-[#00E5FF]">Terms</button>
          </div>
          <div className="text-xs text-slate-500">© 2026 Powered by OpenWord AI</div>
        </div>
      </footer>
    </div>
  );
}
